/** Local hour (24h clock) at which the kiosk performs its daily self-heal reload. */
export const RELOAD_HOUR = 4;

const STORAGE_KEY = 'ts:tv:lastReloadDay';

/** Minimal storage surface so the scheduler is testable without a real Storage. */
export interface ReloadStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Stable per-day key in local time (e.g. "2026-6-8"). */
function dayKey(now: Date): string {
  return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
}

/**
 * Triggers exactly one full-page reload per day, when the wall clock enters
 * {@link RELOAD_HOUR}.
 *
 * Anchored to the toolbar's per-second clock (not a long-lived setTimeout) so it
 * still fires after long uptime / timer drift, and guarded by persistent storage
 * so the post-reload page — which is still within RELOAD_HOUR — does not reload
 * again. That guard is what stops a reload loop for the whole hour.
 */
export class NightlyReloadScheduler {
  constructor(
    private readonly store: ReloadStore,
    private readonly reload: () => void,
  ) {}

  /** Call on every clock tick with the current local time. */
  tick(now: Date): void {
    if (now.getHours() !== RELOAD_HOUR) {
      return;
    }
    const today = dayKey(now);
    if (this.store.getItem(STORAGE_KEY) === today) {
      return; // already reloaded today (the marker survives the reload itself)
    }
    this.store.setItem(STORAGE_KEY, today);
    this.reload();
  }
}
